import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: Abril 2026

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital, incluyendo tu nombre y dirección de correo electrónico.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y confirmar tu compra.
• Enviarte el producto digital adquirido.
• Comunicarnos contigo en caso de ser necesario respecto a tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartición de Datos
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios de pago que procesan tu transacción de forma segura.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación. Puedes configurar tu navegador para rechazar cookies.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento contactándonos directamente.

7. Cambios en esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: Abril 2026

1. Producto Digital
"Vientre Plano SOS: El Reseteo de 24 Horas" es un producto digital informativo en formato PDF. Al completar tu compra, recibirás acceso inmediato al material.

2. Uso Personal
El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa.

3. Resultados
Los resultados descritos son orientativos y pueden variar según cada persona. Este producto no garantiza resultados específicos y no sustituye el consejo médico profesional.

4. Descargo de Responsabilidad Médica
Este producto tiene fines informativos y educativos únicamente. No constituye consejo médico. Consulta siempre a un profesional de la salud antes de realizar cambios en tu alimentación o estilo de vida.

5. Pagos
Todos los pagos se procesan de forma segura a través de plataformas de pago cifradas. Los precios están expresados en dólares estadounidenses (USD).

6. Política de Reembolso
Debido a la naturaleza digital del producto y su entrega inmediata, no se aceptan reembolsos una vez completada la descarga.

7. Propiedad Intelectual
Todo el contenido, incluyendo textos, gráficos y diseño, es propiedad de Vientre Plano SOS y está protegido por las leyes de propiedad intelectual.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del producto implica la aceptación de los términos actualizados.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const getContent = () => {
    if (activeModal === 'privacy') return privacyContent;
    if (activeModal === 'terms') return termsContent;
    return '';
  };
  const getTitle = () => {
    if (activeModal === 'privacy') return 'Política de Privacidad';
    if (activeModal === 'terms') return 'Términos y Condiciones';
    return '';
  };
  return <>
      <footer className="bg-charcoal text-gray-400 py-12 px-4 text-center text-sm" data-id="element-40">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-41">
          <p data-id="element-42">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar. Consulta a tu médico antes de comenzar
            cualquier programa de salud.
          </p>
          <div className="flex justify-center gap-6" data-id="element-43">
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors cursor-pointer" data-id="element-44">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors cursor-pointer" data-id="element-45">
              Política de Privacidad
            </button>
          </div>
          <p className="pt-6 border-t border-gray-700" data-id="element-46">
            © {new Date().getFullYear()} Vientre Plano SOS. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-47">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-48">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-49">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-cream sticky top-0" data-id="element-50">
                <h3 className="text-xl font-bold text-forest font-serif" data-id="element-51">
                  {getTitle()}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-52">
                  <XIcon className="w-5 h-5 text-charcoal" data-id="element-53" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)]" data-id="element-54">
                <div className="text-charcoal-light leading-relaxed whitespace-pre-line text-sm" data-id="element-55">
                  {getContent()}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}